import { Box, Table, LinearProgress, TableContainer, TableCell, TableBody, TableHead, TextField, InputAdornment, TableRow, Typography, Button, Stack, Checkbox, Link } from "@mui/material"
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material"
import { useMemo, useState, useRef, useCallback } from "react"
import LoadingApiQuery from "../../../../../../components/LoadingApiQuery"
import axiosClient from "../../../../../../lib/axios"
import { useQuery } from "react-query"
import useDebounce from "../../../../../../lib/useDebounce"
import SearchIcon from '@mui/icons-material/Search';
import { useFormContext } from "react-hook-form"
import { ProdutoNaoSugerido } from "../../../../../../types/api/estoque/pesquisar-produtos-nao-sugeridos"
import DialogEncomenda from "../../DialogEncomenda"

export default function DialogAdicionar({
    open,
    handleClose
}: DialogAdicionarProps) {

    const { getValues, setValue, watch } = useFormContext()

    const origem_id = Number(watch('filtros.origem')?.id || getValues('filtros.origem')?.id) || 1
    const produto_ids = getValues('items').map((item: ProdutoNaoSugerido) => Number(item.codigo))

    const tableContainerRef = useRef<HTMLDivElement>(null)

    const [limit, setLimit] = useState<number>(50)
    const [keyword, setKeyword] = useState<string>("")
    const [openEncomenda, setOpenEncomenda] = useState<string | null>(null)
    const [selected, setSelected] = useState<ProdutoSelecionado[]>(getValues("nao_sugeridos"))

    //queries
    const debouncedKeyword = useDebounce(keyword)
    const { data, isFetching, isLoading } = useQuery({
        queryKey: ['/pesquisar-produtos-nao-sugeridos', debouncedKeyword, limit, origem_id],
        queryFn: () => axiosClient.get("/estoque/pesquisar-produtos-nao-sugeridos", {
            params: {
                origem_id,
                limit: limit,
                pesquisar: debouncedKeyword,
                produto_ids
            }
        }),
        retry: false,
        keepPreviousData: true,
        cacheTime: 0
    });
    const dadosProdutos: ProdutoNaoSugerido[] = data?.data.data || []
    //

    //funções
    const valorPadrao = useMemo(() => ({ nao_sugerido: true, encomenda: null }), [])

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const bottom = event.currentTarget.scrollHeight - event.currentTarget.scrollTop === event.currentTarget.clientHeight;
        if (bottom && !isFetching) {
            setLimit((prevLimit) => prevLimit + 20)
        }
    };

    const handleChange = useCallback((item: ProdutoNaoSugerido, quantidade: number) => {
        setSelected(prev => {
            const index = prev.findIndex(selectedItem => selectedItem.codigo === item.codigo);
            if (index !== -1) {
                prev[index] = { ...prev[index], quantidade };
                return [...prev];
            } else {
                return [...prev, { ...item, quantidade, ...valorPadrao }];
            }
        });
    }, [valorPadrao])

    const handleChecked = (item: ProdutoNaoSugerido, checked: boolean) => {
        if (checked) setSelected(prev => [...prev, { ...item, quantidade: 0, ...valorPadrao }])
        if (!checked) setSelected(prev => prev.filter(selectedItem => selectedItem.codigo !== item.codigo))
    }

    const handleSubmit = () => {
        setValue('nao_sugeridos', selected)
        handleClose()
    }

    const handleSubmitEncomenda = (encomenda: number | null) => {
        const itemIndex = selected.findIndex(item => item.codigo === openEncomenda);
        if (itemIndex !== -1) {
            const updatedItem = { ...selected[itemIndex], encomenda };
            setSelected(prev => {
                const newSelected = [...prev];
                newSelected[itemIndex] = updatedItem;
                return newSelected;
            });
        }
        setOpenEncomenda(null);
    }
    //

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle>
                {"Adicionar itens não sugeridos"}
            </DialogTitle>

            <DialogContent>
                <Box py={2}>
                    <TextField
                        fullWidth
                        value={keyword}
                        label="Pesquisar"
                        onChange={({ target }) => setKeyword(target.value)}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }
                        }}
                    />

                    {isLoading && <LoadingApiQuery extraParams={{ height: 400, my: 1, alignItems: "center" }} />}

                    <Box mt={1}>{(isFetching && data) && <LinearProgress />}</Box>
                    {
                        (!isLoading && data) &&
                        <TableContainer
                            ref={tableContainerRef}
                            onScroll={handleScroll}
                            sx={{ height: 400, overflow: "auto", mb: 1 }}
                        >

                            <Table size="small" aria-label="a dense table" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Código</b></TableCell>
                                        <TableCell><b>Nome</b></TableCell>
                                        <TableCell align="right"><b>Qtd. origem</b></TableCell>
                                        <TableCell align="right"><b>Solicitar</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dadosProdutos.map((row, index) => {
                                        const selecionado = selected.find(({ codigo }) => codigo === row.codigo);

                                        return (
                                            <TableRow
                                                key={row.codigo}
                                                sx={{
                                                    cursor: "pointer",
                                                    backgroundColor: selecionado ? "#edf7ed" : "unset",
                                                    '&:last-child td, &:last-child th': { border: 0 }
                                                }}
                                            >
                                                <TableCell>{row.codigo}</TableCell>
                                                <TableCell>
                                                    <Typography variant="subtitle2">{row.nome}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {row.departamento} / {row.setor}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">{row.estoque_origem}</TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        <Checkbox
                                                            checked={selecionado ? true : false}
                                                            onChange={
                                                                (event: React.ChangeEvent<HTMLInputElement>) =>
                                                                    handleChecked(row, event.target.checked)
                                                            }
                                                        />
                                                        <TextField
                                                            size="small"
                                                            variant="outlined"
                                                            value={selecionado?.quantidade}
                                                            onChange={
                                                                (event: React.ChangeEvent<HTMLInputElement>) =>
                                                                    handleChange(row, Number(event.target.value))
                                                            }
                                                            slotProps={{
                                                                htmlInput: { tabIndex: index + 1 },
                                                                inputLabel: { shrink: selecionado ? true : false },
                                                                input: { endAdornment: <InputAdornment position="end">{row.unidade_medida}</InputAdornment>, }
                                                            }}
                                                            sx={{ maxWidth: 125 }}
                                                        />
                                                    </Stack>
                                                    {
                                                        selecionado && !selecionado.encomenda &&
                                                        <Box
                                                            width={'100%'}
                                                            display={'flex'}
                                                            justifyContent={'flex-end'}
                                                        >
                                                            <Link
                                                                fontSize={12}
                                                                textAlign={'right'}
                                                                justifyContent={"flex-end"}
                                                                onClick={() => setOpenEncomenda(row.codigo)}
                                                            >
                                                                É encomenda?
                                                            </Link>
                                                        </Box>

                                                    }

                                                    {
                                                        selecionado && selecionado.encomenda &&
                                                        <Box
                                                            width={'100%'}
                                                        >
                                                            <Link
                                                                width={'100%'}
                                                                fontSize={11}
                                                                textAlign={'right'}
                                                                justifyContent={"flex-end"}
                                                                onClick={() => setOpenEncomenda(row.codigo)}
                                                            >
                                                                Encomenda: {selecionado.encomenda} {row.unidade_medida}
                                                            </Link>
                                                        </Box>

                                                    }
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    }

                    {
                        openEncomenda &&
                        <DialogEncomenda
                            open={Boolean(openEncomenda)}
                            handleClose={() => setOpenEncomenda(null)}
                            selecionado={selected.find((i: ProdutoSelecionado) => i.codigo === openEncomenda)}
                            onSubmit={(encomenda: number | null) => handleSubmitEncomenda(encomenda)}
                        />
                    }
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleClose}
                >
                    Cancelar
                </Button>

                <Button
                    autoFocus
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={selected.length === 0}
                >
                    Confirmar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface DialogAdicionarProps {
    open: boolean,
    handleClose: () => void
}

export interface ProdutoSelecionado extends ProdutoNaoSugerido {
    quantidade: number,
    nao_sugerido: boolean,
    encomenda: number | null
}
